/**
 * 공유 상품(TEMPLATE)으로 발급된 수강권의 **현재** 공유 단계 판정.
 *
 * 예전에는 `membership.sharePolicySnapshot.enabled` 하나가 두 가지를 겸했다.
 *   (1) "공유 상품으로 발급됐다" — 모델에서 지워지지 않는 **영구 사실**
 *   (2) "지금 공유 상품으로 보호받아야 한다" — **현재 상태**
 * 그래서 공유를 8분 만에 끄고 참여자가 0명이어도 수정·재공유가 영구히 막혔다.
 *
 * 이제 서버가 둘을 갈라 `shareLifecycle` 로 내려준다. 화면은 **추론하지 않고 그대로 읽는다.**
 * 근거: 백엔드 `membershipShareProtection.service.ts` 의 `buildTemplateShareEditabilityView`.
 */

/**
 * 서버가 내려주는 단계. **직접 분기하지 않는다** — 값이 늘어도 화면이 깨지지 않게
 * `canEditGeneralFields` / `canReshare` 두 불리언만 쓴다. 여기 있는 목록은 안내 문구를
 * 다듬거나 디버깅할 때 참고용이고, 모르는 값이 와도 타입이 막지 않도록 열어 둔다.
 */
export type ShareLifecycleStage =
    | 'NONE'
    | 'PROVISIONING'
    | 'ACTIVE_HEALTHY'
    | 'ACTIVE_INCONSISTENT'
    | 'CLOSED_STABLE'
    | 'INCONSISTENT'
    | (string & {});

/** `CLOSED_STABLE` 일 때만 온다. 총 회차를 어디까지 줄일 수 있는지. */
export type ShareLifecycleUsage = {
    capacity: number;
    usedCount: number;
    futureScheduledCount: number;
    pendingRequestCount: number;
    /**
     * 과거 참여자가 쓴 회차를 침범하지 않는 **총 회차 하한**.
     * 서버가 `startSessionValue + (사용 + 미래예약 + 대기신청) - 1` 로 계산해 내려준 값이다.
     * 프론트에서 다시 계산하지 않는다.
     */
    minimumTotalSession: number;
};

export type ShareLifecycle = {
    stage: ShareLifecycleStage;
    isTemplateIssued: boolean;
    canEditGeneralFields: boolean;
    canReshare: boolean;
    /** 종료 뒤에도 영구히 못 바꾸는 발급 원본 필드. */
    lockedFields: string[];
    /** 바꾸면 지나간 달의 정산·매출 숫자가 함께 달라지는 필드. */
    settlementImpactFields: string[];
    /** 공유 **중**에도 서버가 허용하는 필드. */
    editableWhileSharedFields: string[];
    /**
     * 바꾸면 **공유 참여자**에게 영향이 가는 필드. 정산 축과 겹치지 않는다.
     * (이용 기간·만료 정책·수업 시간 — 참여자의 예약 가능 범위와 슬롯 길이를 정한다.)
     */
    participantImpactFields: string[];
    sharedUsage?: ShareLifecycleUsage;
};

const readBoolean = (value: unknown): boolean | undefined =>
    typeof value === 'boolean' ? value : undefined;

const readStringList = (value: unknown): string[] =>
    Array.isArray(value)
        ? value
              .map((item) => (typeof item === 'string' ? item.trim() : ''))
              .filter((item) => item.length > 0)
        : [];

const readCount = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;

const readSharedUsage = (value: unknown): ShareLifecycleUsage | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const raw = value as Record<string, unknown>;
    const capacity = readCount(raw.capacity);
    const usedCount = readCount(raw.usedCount);
    const futureScheduledCount = readCount(raw.futureScheduledCount);
    const pendingRequestCount = readCount(raw.pendingRequestCount);
    const minimumTotalSession = readCount(raw.minimumTotalSession);
    if (
        capacity === undefined ||
        usedCount === undefined ||
        futureScheduledCount === undefined ||
        pendingRequestCount === undefined ||
        minimumTotalSession === undefined
    ) {
        // 하나라도 어긋나면 하한을 걸지 않는다. 잘못된 하한은 정상 수정을 화면에서만
        // 막아버려, 서버가 허용하는 저장을 사람이 못 하게 만든다.
        return undefined;
    }
    return {
        capacity,
        usedCount,
        futureScheduledCount,
        pendingRequestCount,
        minimumTotalSession,
    };
};

/**
 * `GET /v2/memberships/:id` · `PATCH /v3/memberships/:id` 응답 본문에서 `shareLifecycle` 을 읽는다.
 *
 * 키가 **아예 없을 수 있다.** 두 경우다.
 *   1. 구버전 서버 (프론트가 먼저 배포된 경우)
 *   2. 서버가 판정에 실패한 경우 — 상세를 실패시키지 않고 키만 생략한다
 * 두 경우 모두 `null` 을 돌려 호출부가 폴백(더 엄격한 쪽)을 쓰게 한다.
 *
 * 세 불리언 중 하나라도 빠지면 부분 신뢰하지 않고 통째로 `null` 이다. 반쪽짜리 판정으로
 * 잠긴 화면을 여는 것보다 예전 동작을 유지하는 쪽이 안전하다.
 */
export const readShareLifecycle = (body: unknown): ShareLifecycle | null => {
    if (!body || typeof body !== 'object') return null;
    const raw = (body as { shareLifecycle?: unknown }).shareLifecycle;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    const lifecycle = raw as Record<string, unknown>;
    const isTemplateIssued = readBoolean(lifecycle.isTemplateIssued);
    const canEditGeneralFields = readBoolean(lifecycle.canEditGeneralFields);
    const canReshare = readBoolean(lifecycle.canReshare);
    if (
        isTemplateIssued === undefined ||
        canEditGeneralFields === undefined ||
        canReshare === undefined
    ) {
        return null;
    }

    return {
        stage: typeof lifecycle.stage === 'string' ? lifecycle.stage : 'NONE',
        isTemplateIssued,
        canEditGeneralFields,
        canReshare,
        lockedFields: readStringList(lifecycle.lockedFields),
        settlementImpactFields: readStringList(lifecycle.settlementImpactFields),
        editableWhileSharedFields: readStringList(lifecycle.editableWhileSharedFields),
        participantImpactFields: readStringList(lifecycle.participantImpactFields),
        sharedUsage: readSharedUsage(lifecycle.sharedUsage),
    };
};

/** 화면이 실제로 쓰는 형태. 서버 판정이 없으면 폴백이 이미 적용돼 있다. */
export type ShareEditability = {
    /**
     * 서버 판정을 실제로 받았는지. **게이트 결정에 쓰지 않는다** — 안내 문구가
     * "공유를 종료하면 수정할 수 있다"고 약속해도 되는지를 가르는 데만 쓴다.
     * 구버전 서버에서는 종료해도 계속 막히므로 그 약속이 거짓이 된다.
     */
    hasServerJudgement: boolean;
    /** 공유 상품으로 발급된 건인지(영구 사실). */
    isTemplateIssued: boolean;
    /** 회차·기간·금액 등 일반 수정 가능 여부. 공유 상품 축만 본다. */
    canEditGeneralFields: boolean;
    /** "다시 공유 시작" 버튼을 그려도 되는지. */
    canReshare: boolean;
    /** 바꾸면 지나간 달 정산이 달라지는 필드. 없으면 빈 배열. */
    settlementImpactFields: string[];
    /**
     * 공유 **중**에도 서버가 저장을 허용하는 필드(서버 `editableWhileSharedFields`).
     *
     * 서버 판정이 없으면 **빈 배열**이다. "모른다"와 "허용된 게 없다"를 같게 다룬다 —
     * 둘 다 화면이 입력을 열면 안 되는 상태라서 결과가 같고, 갈라 두면 호출부가
     * "모르면 일단 열자"로 잘못 쓸 여지가 생긴다.
     */
    editableWhileSharedFields: string[];
    /**
     * 바꾸면 공유 참여자에게 영향이 가는 필드(서버 `participantImpactFields`).
     *
     * **계약이다**(백엔드 확인, 2026-09-01) — best-effort 힌트가 아니라 판정에 써도 된다.
     * 없으면 빈 배열이고, 그때는 **경고만 생략한다. 저장을 막지 않는다** — 경고는 판정이
     * 아니라서, 못 띄우는 것과 하면 안 되는 것은 다르다.
     */
    participantImpactFields: string[];
    /**
     * 공유 사용량 스냅샷. 모르면 null.
     *
     * 총 회차 하한과 그 근거(사용·예약·대기 건수)를 함께 담는다. 안내 문구에 숫자를
     * 넣으려면 하한만으로는 부족하다 — "12회 아래로 못 줄인다"에 이어 "12회가 어디서
     * 나왔는지"까지 말해야 관리자가 무엇을 정리할지 안다.
     */
    sharedUsage: ShareLifecycleUsage | null;
    /** 총 회차 하한. 모르면 null — 하한을 걸지 않고 서버 판정에 맡긴다. */
    minimumTotalSession: number | null;
};

/**
 * `shareLifecycle` 이 없으면 **예전 추론으로 폴백한다. 더 엄격한 쪽이라 안전하다.**
 * (핸드오프 §2 "shareLifecycle 이 없을 때 — 반드시 처리할 것" 의 코드를 그대로 옮겼다.)
 *
 * - `canEditGeneralFields` 가 없으면 기존처럼 **막는다**.
 * - `canReshare` 가 없으면 **버튼을 그리지 않는다**.
 * - `settlementImpactFields` 가 없으면 경고를 못 띄우는 것이지, 저장을 막을 필요는 없다.
 * - `editableWhileSharedFields` 가 없으면 공유 중 **부분 편집도 열지 않는다**(전면 차단 유지).
 * - `sharedUsage` 가 없으면 회차 입력을 열지 않는다. 하한을 모르는 채로 열면 사용량
 *   아래로 줄이는 요청이 서버까지 가서, 눌러야만 409 가 보이는 자리가 생긴다.
 */
export const resolveShareEditability = ({
    lifecycle,
    sharePolicySnapshotEnabled,
}: {
    lifecycle: ShareLifecycle | null | undefined;
    /** `membership.sharePolicySnapshot?.enabled === true` — 구버전 폴백의 유일한 근거. */
    sharePolicySnapshotEnabled: boolean;
}): ShareEditability => {
    const isTemplateIssued = lifecycle?.isTemplateIssued ?? sharePolicySnapshotEnabled;
    return {
        hasServerJudgement: !!lifecycle,
        isTemplateIssued,
        canEditGeneralFields: lifecycle?.canEditGeneralFields ?? !isTemplateIssued,
        canReshare: lifecycle?.canReshare ?? false,
        settlementImpactFields: lifecycle?.settlementImpactFields ?? [],
        editableWhileSharedFields: lifecycle?.editableWhileSharedFields ?? [],
        participantImpactFields: lifecycle?.participantImpactFields ?? [],
        sharedUsage: lifecycle?.sharedUsage ?? null,
        minimumTotalSession: lifecycle?.sharedUsage?.minimumTotalSession ?? null,
    };
};
